/**
 * input.ts — mouse + keyboard input → UiState mutation.
 *
 * Explicitly NOT a Command: selection, camera, and view mode live in UiState,
 * which is per-client and never synchronised across peers (MULTIPLAYER §2).
 *
 * Galaxy view:
 *   - Wheel:           zoom galaxyCamera toward the cursor. Past
 *                      GALAXY_TO_SYSTEM_ZOOM, auto-transition to the in-system
 *                      view for the nearest system to the viewport centre.
 *   - Left-drag:       pan galaxyCamera.
 *   - Middle-drag:     pan galaxyCamera.
 *   - Left-click:      select the nearest system within a pixel threshold; if
 *                      none, clear selection.
 *   - Double-click:    enter the in-system view for the clicked system.
 *
 * System (tactical) view:
 *   - Wheel:           zoom systemCamera toward the cursor. Zooming out past
 *                      SYSTEM_VIEW_EXIT_ZOOM transitions back to galaxy view.
 *   - Left-drag:       pan systemCamera (primary pan gesture). Middle-drag
 *                      also pans for muscle-memory parity with the galaxy
 *                      view.
 *   - Shift+Left-drag: drag-to-select marquee — RTS-convention, always
 *                      additive (adds to the existing selection rather than
 *                      replacing it).
 *   - Left-click:      select the topmost interactive element at the cursor,
 *                      priority: ship → linked JP (jumps selected ships) →
 *                      planet → empty (clear selection). Shift-click toggles
 *                      the clicked ship in/out of the multi-selection instead
 *                      of replacing it.
 *   - Right-click:     issue a move order to the currently selected player
 *                      ships, targeting the in-system position under the
 *                      cursor. No-op when nothing is selected.
 *
 * Globals:
 *   - G / g:           return to galaxy view (cross-fade).
 *   - C / c:           toggle cloak on the current ship selection. Emits one
 *                      CommandToggleCloak — applier filters non-eligible ships
 *                      silently (wrong empire, no template canCloak, no
 *                      researched Cloaking tech, mid-jump, EMP-disabled).
 *   - Escape:          clear selection.
 */

import type { GameState, ShipInstanceId } from '../state/types/index';
import type { Command } from '../game/commands';
import { WARP_GATE_CHASSIS } from '../game/balance-space-structures';
import { hitTestSystems, panByScreenDelta, screenToWorld, zoomAt, type Viewport } from '../render/camera';
import { saveGalaxyOverlay } from '../render/galaxy-overlays';
import { classifyShipForViewer, inFlightShipScreenPos } from '../render/galaxy-paths';
import {
  assignFleet,
  centerCameraOnFleet,
  clampZoom,
  defaultSystemCamera,
  exitDevSpawnMode,
  GALAXY_TO_SYSTEM_ZOOM,
  marqueeBounds,
  nearestSystemToCamera,
  recallFleet,
  startViewTransition,
  SYSTEM_TO_GALAXY_ZOOM,
  SYSTEM_VIEW_EXIT_ZOOM,
  type UiState,
} from './state';
import { devSpawnEntity } from '../game/dev-spawn-entity';
import {
  bindingMatchesEvent,
  getKeyForAction,
  type ActionId,
} from '../settings/keybindings';
import {
  hitTestSystemViewEnvironment,
  hitTestSystemViewPlanets,
  hitTestSystemViewShips,
  layoutSystemView,
  shipsInScreenRect,
  SHIP_HIT_RADIUS_PX,
} from '../render/system-view';

export interface InputHandlerOptions {
  canvas: HTMLCanvasElement;
  uiState: UiState;
  getGameState: () => GameState;
  getViewport: () => Viewport;
  /**
   * Optional sink for sim Commands issued by clicks (jumpShips, moveShips).
   * Omitted in tests that only care about UiState mutations.
   */
  queueCommand?: (cmd: Command) => void;
  /**
   * Fired when Esc is pressed with nothing to dismiss (no placement mode,
   * no selection, no marquee). Session wires this to open the in-game menu
   * overlay per UI_UX §9.3. Omitted in tests.
   */
  onOpenMenu?: () => void;
  /**
   * Game-speed controller (v2M3) — receives Space (toggle pause), '+' (cycle
   * up), '-' (cycle down). Omitted in tests that don't care about speed.
   */
  speedController?: import('../game/speed-controller').SpeedController;
  /**
   * v4M1.5 — live accessor for the `controls` Settings sub-object.
   * Read each wheel event / G-key press so toggles take effect mid-
   * session without a restart. Omitted in tests; defaults match the
   * legacy hardcoded behaviour (1× zoom rate, G-as-toggle).
   */
  getControlsSettings?: () => import('../settings/types').ControlsSettings;
}

export interface InputHandlerHandle {
  destroy(): void;
}

const CLICK_SLOP_PX = 4;
// MFix6.6 — star sprites are ~25–52 px radius. Hit target covers the
// visible sprite plus a generous halo so the click feels forgiving
// even at minimum zoom.
const SYSTEM_HIT_RADIUS_PX = 55;
// v4M6.6 — in-flight ship dots render at 3 px with a 7 px halo. Hit
// radius is generous to let the cursor land on small-zoom transits
// without pixel-perfect precision.
const IN_FLIGHT_HOVER_RADIUS_PX = 14;

/**
 * Transient drag state — `button` is the MouseEvent.button value that started
 * the drag. `mode` is resolved lazily on the first movement past CLICK_SLOP_PX:
 *   - 'pending' while we don't yet know whether the gesture is a click or a
 *     drag. mouseup before the slop threshold → click; after → drag.
 *   - 'pan' — left-drag in galaxy view or middle-drag anywhere.
 *   - 'marquee' — left-drag in tactical view.
 */
type DragMode = 'pending' | 'pan' | 'marquee';

export function attachInputHandlers(opts: InputHandlerOptions): InputHandlerHandle {
  const { canvas, uiState, getGameState, getViewport, queueCommand, onOpenMenu, speedController, getControlsSettings } = opts;

  let mouseDownAt: { x: number; y: number } | null = null;
  let lastMousePos: { x: number; y: number } | null = null;
  let dragButton: number | null = null;
  let dragMode: DragMode = 'pending';
  let dragShiftAtStart = false;
  // v3M16 — Double-tap N tracking for the fleet camera-jump gesture.
  // Reset whenever the second tap window closes or a different fleet
  // gets tapped.
  let lastFleetTapId: number | null = null;
  let lastFleetTapTime = 0;
  /** Window for treating successive same-fleet taps as a "double-tap". */
  const FLEET_DOUBLE_TAP_MS = 300;

  function localMousePos(e: MouseEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function now(): number {
    return performance.now();
  }

  // v4M1.5 — hold-G-to-preview-galaxy state. Tracks the most recent
  // G-induced view switch so the keyup handler can restore the prior
  // focus. Inactive when the toggle is off (default) — G then behaves
  // as a one-shot switch as it always has.
  const holdGPreview: {
    active: boolean;
    previousSystemId: number | null;
  } = { active: false, previousSystemId: null };

  function onKeyUp(e: KeyboardEvent): void {
    if (e.key === 'g' || e.key === 'G') {
      if (!holdGPreview.active) return;
      holdGPreview.active = false;
      // Restore the prior focused system + system view. If the player
      // had no prior focus (e.g. opened galaxy fresh and held G), stay
      // in galaxy view.
      if (holdGPreview.previousSystemId !== null) {
        uiState.focusedSystemId = holdGPreview.previousSystemId;
        startViewTransition(uiState, 'system', now());
      }
    }
  }

  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    const anchor = localMousePos(e);
    const step = Math.sign(e.deltaY);

    // Normalise deltaY across devices by sign; 1.1^(step) gives a pleasant
    // per-notch feel in either direction. v4M1.5 — `Controls →
    // Smooth-zoom speed` slider widens or narrows the per-notch
    // multiplier so a player who wants finer/coarser zooming can tune
    // it. Default 1.0 keeps the legacy 1.1× per notch.
    const speed = getControlsSettings?.().smoothZoomSpeed ?? 1.0;
    const basePerNotch = 1 + 0.1 * speed;
    const factor = step > 0 ? 1 / basePerNotch : step < 0 ? basePerNotch : 1;

    if (uiState.viewMode === 'galaxy') {
      uiState.galaxyCamera = zoomAt(
        uiState.galaxyCamera,
        getViewport(),
        anchor,
        factor,
        clampZoom,
      );

      // Crossed the galaxy→system threshold? Pick a focus target and fade in
      // the tactical view. Prefer the system nearest the CURSOR in screen
      // space (the zoom anchor) — when two systems are close together on the
      // map, the camera centre can drift toward the "wrong" one after the
      // zoom step, but the cursor always points at the system the player
      // actually meant. Fall back to camera-centre nearest if the cursor
      // isn't pointing at anything (e.g. keyboard-driven zoom in a future
      // input mode).
      if (uiState.galaxyCamera.zoom >= GALAXY_TO_SYSTEM_ZOOM) {
        const state = getGameState();
        const vp = getViewport();
        const cursorHit = hitTestSystems(
          uiState.galaxyCamera,
          vp,
          state.systems.values(),
          anchor,
          Number.POSITIVE_INFINITY,
        );
        const target =
          cursorHit?.id ??
          nearestSystemToCamera(state.systems.values(), uiState.galaxyCamera);
        if (target !== null) {
          // v4M10.3 — drill-in is unconditional per `FOW.md §3.4`. The
          // v4M6.5 hard gate on unscouted systems is retired: entry is
          // allowed and the renderer's Unexplored branch
          // (`src/render/system-view.ts` + `currentLayerForSystem`)
          // draws the system as a black void with the central star,
          // doing the work the toast used to do.
          uiState.focusedSystemId = target;
          // Reset the in-system camera so the tactical map opens framed —
          // whole system boundary visible, centred on the star.
          uiState.systemCamera = defaultSystemCamera();
          startViewTransition(uiState, 'system', now());
        }
      }
    } else {
      // System view — wheel zooms the in-system camera around the cursor.
      // Zooming out past the exit floor trips a transition back to galaxy.
      const nextZoom = uiState.systemCamera.zoom * factor;
      if (factor < 1 && nextZoom < SYSTEM_VIEW_EXIT_ZOOM) {
        // Reset the system camera to its default framing so re-entering the
        // view doesn't start from a weird zoom.
        uiState.systemCamera = defaultSystemCamera();
        // Drop galaxy zoom comfortably below the entry threshold so we don't
        // immediately trip the galaxy→system switch again on the next wheel
        // notch.
        uiState.galaxyCamera = {
          ...uiState.galaxyCamera,
          zoom: clampZoom(SYSTEM_TO_GALAXY_ZOOM - 0.5),
        };
        startViewTransition(uiState, 'galaxy', now());
      } else {
        uiState.systemCamera = zoomAt(
          uiState.systemCamera,
          getViewport(),
          anchor,
          factor,
          clampZoom,
        );
      }
    }
  }

  function onMouseDown(e: MouseEvent): void {
    // We care about left (0), middle (1), right (2). Extra buttons are ignored.
    if (e.button !== 0 && e.button !== 1 && e.button !== 2) return;

    const p = localMousePos(e);
    mouseDownAt = p;
    lastMousePos = p;
    dragButton = e.button;
    dragMode = 'pending';
    dragShiftAtStart = e.shiftKey;
    uiState.isDragging = false;

    // Middle-mouse is committed to pan immediately — middle-click has no
    // click-select semantics in either view, so there's no pending phase.
    if (e.button === 1) {
      dragMode = 'pan';
      e.preventDefault(); // browsers use middle-click for autoscroll
    }
  }

  function onMouseMove(e: MouseEvent): void {
    const p = localMousePos(e);

    // v4M5.3b3e — galaxy-view hover tracking drives the jump-preview
    // tooltip (destination name + fuel cost for the currently-selected
    // fleet). System view hover is retired; the system-view tooltip
    // surface re-enters via charge-up indicators on ship sprites.
    updateHoveredGalaxySystem(p);
    updateCursorWorld(p);

    if (!mouseDownAt || !lastMousePos || dragButton === null) return;
    const dx = p.x - lastMousePos.x;
    const dy = p.y - lastMousePos.y;
    const totalDx = p.x - mouseDownAt.x;
    const totalDy = p.y - mouseDownAt.y;

    // Promote from pending → drag once we've crossed the click slop.
    if (!uiState.isDragging && Math.hypot(totalDx, totalDy) > CLICK_SLOP_PX) {
      uiState.isDragging = true;
      if (dragMode === 'pending') {
        if (dragButton === 0) {
          // MFix5.5 — left-drag.
          //   - Galaxy view: always pan (galaxy navigation depends on
          //     LMB-drag and there's nothing to box-select up there).
          //   - Tactical view: marquee-select by default (RTS convention).
          //     Shift held at drag start makes it additive. Pan in
          //     tactical view now flows through RMB-drag instead.
          if (uiState.viewMode === 'galaxy') {
            dragMode = 'pan';
          } else {
            dragMode = 'marquee';
            uiState.marquee = {
              startX: mouseDownAt.x,
              startY: mouseDownAt.y,
              currentX: p.x,
              currentY: p.y,
              additive: dragShiftAtStart,
            };
          }
        } else if (dragButton === 2) {
          // MFix5.5 — right-drag pans in any view. RMB click without
          // drag still routes to handleRightClick (move / jump / colonise
          // / bombard) via the wasDragging guard in onMouseUp.
          dragMode = 'pan';
        }
      }
    }

    if (!uiState.isDragging) {
      lastMousePos = p;
      return;
    }

    if (dragMode === 'pan') {
      if (uiState.viewMode === 'galaxy') {
        uiState.galaxyCamera = panByScreenDelta(
          uiState.galaxyCamera,
          getViewport(),
          dx,
          dy,
        );
      } else {
        uiState.systemCamera = panByScreenDelta(
          uiState.systemCamera,
          getViewport(),
          dx,
          dy,
        );
      }
    } else if (dragMode === 'marquee' && uiState.marquee) {
      uiState.marquee.currentX = p.x;
      uiState.marquee.currentY = p.y;
    }

    lastMousePos = p;
  }

  function onMouseLeave(): void {
    uiState.cursorWorld = null;
    uiState.hoveredGalaxySystemId = null;
    uiState.hoveredGalaxyAnchor = null;
    uiState.hoveredInFlightShipId = null;
  }

  /**
   * v4M5.3b3e — track which star the cursor is hovering in galaxy
   * view so the jump-preview tooltip can resolve destination + cost.
   * Clears outside the galaxy view or when no system is within
   * `SYSTEM_HIT_RADIUS_PX` of the cursor.
   */
  function updateHoveredGalaxySystem(p: { x: number; y: number }): void {
    if (uiState.viewMode !== 'galaxy') {
      uiState.hoveredGalaxySystemId = null;
      uiState.hoveredGalaxyAnchor = null;
      uiState.hoveredInFlightShipId = null;
      return;
    }
    const state = getGameState();
    const vp = getViewport();
    const hit = hitTestSystems(
      uiState.galaxyCamera,
      vp,
      state.systems.values(),
      { x: p.x, y: p.y },
      SYSTEM_HIT_RADIUS_PX,
    );
    if (hit) {
      uiState.hoveredGalaxySystemId = hit.id;
      uiState.hoveredGalaxyAnchor = { x: p.x, y: p.y };
      // System hover wins over in-flight ship hover — the system has
      // the bigger target and the player most likely meant the system.
      uiState.hoveredInFlightShipId = null;
    } else {
      uiState.hoveredGalaxySystemId = null;
      uiState.hoveredGalaxyAnchor = null;
      // v4M6.6 — system miss → check for in-flight ship hover. Hit-test
      // walks every visible mid-jump ship and picks the first one
      // within `IN_FLIGHT_HOVER_RADIUS_PX` of the cursor (Map iteration
      // is insertion-ordered, deterministic). Cleared when no ship is
      // in range. Anchor stored so the tooltip widget can position
      // itself near the cursor without re-deriving.
      const hoveredShipId = hitTestInFlightShips(state, uiState, vp, p);
      uiState.hoveredInFlightShipId = hoveredShipId;
      uiState.hoveredGalaxyAnchor = hoveredShipId !== null ? { x: p.x, y: p.y } : null;
    }
  }

  /**
   * v4M6.6 — hit-test the cursor against in-flight ship screen dots.
   * Returns the id of the first visible (own / teammate / visible-enemy
   * per `classifyShipForViewer`) ship whose interpolated screen position
   * is within `IN_FLIGHT_HOVER_RADIUS_PX` of the cursor. Returns null
   * when no match. Pure reader — never mutates state.
   *
   * Shared interpolation math via `inFlightShipScreenPos` so this and
   * the `galaxy-view.ts` dot render path agree exactly on where the
   * hit-test target sits.
   */
  function hitTestInFlightShips(
    state: import('../state/types').GameState,
    ui: UiState,
    vp: Viewport,
    cursor: { x: number; y: number },
  ): number | null {
    const radiusSq = IN_FLIGHT_HOVER_RADIUS_PX * IN_FLIGHT_HOVER_RADIUS_PX;
    for (const ship of state.ships.values()) {
      const cls = classifyShipForViewer(state, ui, ship);
      if (cls === 'hidden') continue;
      const screen = inFlightShipScreenPos(state, ship, ui.galaxyCamera, vp);
      if (!screen) continue;
      const dx = screen.x - cursor.x;
      const dy = screen.y - cursor.y;
      if (dx * dx + dy * dy <= radiusSq) return ship.id;
    }
    return null;
  }

  /**
   * Track the cursor's world position in the tactical view so placement-mode
   * consumers (M14 build ghost) can draw at the cursor without re-deriving
   * from screen coords. Sets null outside the tactical view or when layout
   * isn't available (e.g. mid-transition).
   */
  function updateCursorWorld(p: { x: number; y: number }): void {
    if (uiState.viewMode !== 'system' || uiState.focusedSystemId === null) {
      uiState.cursorWorld = null;
      return;
    }
    const state = getGameState();
    const vp = getViewport();
    const layout = layoutSystemView(state, uiState, vp);
    if (!layout) {
      uiState.cursorWorld = null;
      return;
    }
    const world = screenToWorld(uiState.systemCamera, layout.squareVp, p);
    uiState.cursorWorld = {
      sx: world.x,
      sy: world.y,
      systemId: uiState.focusedSystemId,
    };
  }

  function onMouseUp(e: MouseEvent): void {
    if (e.button !== 0 && e.button !== 1 && e.button !== 2) return;
    if (!mouseDownAt || dragButton !== e.button) return;
    const p = localMousePos(e);
    const travelled = Math.hypot(p.x - mouseDownAt.x, p.y - mouseDownAt.y);
    const wasDragging = uiState.isDragging;
    const shift = e.shiftKey;

    if (dragMode === 'marquee' && uiState.marquee) {
      // Finalise marquee: every ship whose centroid fell inside the rect at
      // mouseup joins the selection (replacing when !additive).
      finalizeMarquee(uiState.marquee);
      uiState.marquee = null;
    } else if (!wasDragging && travelled <= CLICK_SLOP_PX) {
      // No movement — resolve as a click on the originating button.
      if (e.button === 0) handleLeftClick(p, shift);
      else if (e.button === 2) handleRightClick(p, shift);
    }

    mouseDownAt = null;
    lastMousePos = null;
    dragButton = null;
    dragMode = 'pending';
    uiState.isDragging = false;
  }

  function finalizeMarquee(m: {
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    additive: boolean;
  }): void {
    const state = getGameState();
    const vp = getViewport();
    const layout = layoutSystemView(state, uiState, vp);
    if (!layout) return;
    const b = marqueeBounds(m);
    const hits = shipsInScreenRect(layout, b.minX, b.minY, b.maxX, b.maxY);
    // Restrict multi-select to player-owned ships in the focused system —
    // selecting an enemy ship has no actionable follow-up in v1.
    const playerHits = hits.filter((id) => {
      const ship = state.ships.get(id);
      return !!ship && ship.ownerEmpireId === state.playerEmpireId;
    });
    if (!m.additive) uiState.selectedShipIds = new Set();
    for (const id of playerHits) uiState.selectedShipIds.add(id);
    reconcileSelectionWithShipSet();
  }

  /**
   * Keep the legacy `selection` field usable by the detail panel: mirror it
   * to the only selected ship when exactly one ship is selected, and blank it
   * on zero/many so the panel doesn't show stale info.
   */
  function reconcileSelectionWithShipSet(): void {
    if (uiState.selectedShipIds.size === 1) {
      const id = uiState.selectedShipIds.values().next().value as ShipInstanceId;
      uiState.selection = { kind: 'ship', id };
    } else if (uiState.selectedShipIds.size > 1) {
      // Multi-select — hide single-entity detail until collapsed to one.
      uiState.selection = { kind: 'none' };
    }
  }

  function handleLeftClick(p: { x: number; y: number }, shift: boolean): void {
    const state = getGameState();
    const vp = getViewport();

    // v3M22.4 — Guard target-picker intercept. When a Guard mode is
    // active (player clicked Guard on a ship's detail panel), the next
    // LMB on a same-system planet or friendly ship locks the guard
    // target. LMB elsewhere (empty space, JP, terrain) is a no-op so
    // the player can keep aiming. Esc / RMB cancel the mode (handled
    // elsewhere).
    if (uiState.guardTargetMode !== null && uiState.viewMode === 'system') {
      const guardMode = uiState.guardTargetMode;
      const layout = layoutSystemView(state, uiState, vp);
      if (layout && queueCommand) {
        const guardingShip = state.ships.get(guardMode.shipId);
        if (!guardingShip || guardingShip.systemId !== guardMode.systemId) {
          // Guarding ship vanished or jumped — abort silently.
          uiState.guardTargetMode = null;
          return;
        }
        // Try ship hit first (smaller, easier to overlap with planet hit).
        const targetShipId = hitTestSystemViewShips(layout, p.x, p.y, SHIP_HIT_RADIUS_PX);
        if (targetShipId !== null) {
          const target = state.ships.get(targetShipId);
          if (
            target &&
            target.ownerEmpireId === state.playerEmpireId &&
            target.id !== guardMode.shipId &&
            target.systemId === guardMode.systemId
          ) {
            queueCommand({
              kind: 'setShipGuard',
              empireId: state.playerEmpireId,
              shipId: guardMode.shipId,
              targetKind: 'ship',
              targetId: targetShipId,
            });
            uiState.guardTargetMode = null;
            return;
          }
        }
        // Try planet hit.
        const targetPlanetId = hitTestSystemViewPlanets(layout, p.x, p.y);
        if (targetPlanetId !== null) {
          const planet = state.planets.get(targetPlanetId);
          if (planet && planet.systemId === guardMode.systemId) {
            queueCommand({
              kind: 'setShipGuard',
              empireId: state.playerEmpireId,
              shipId: guardMode.shipId,
              targetKind: 'planet',
              targetId: targetPlanetId,
            });
            uiState.guardTargetMode = null;
            return;
          }
        }
        // Empty / invalid click — do nothing, keep the picker active.
        return;
      }
    }

    // MFix11.3 — Dev Toolbox spawn-entity intercept. Multi-click model:
    // each click spawns one entity at the cursor world coords; the mode
    // stays active for subsequent clicks until toggled off or Escape
    // pressed. Fires BEFORE buildPlacement so the dev tool wins when
    // both modes happen to be active.
    if (uiState.devSpawnMode !== null && uiState.viewMode === 'system') {
      const layout = layoutSystemView(state, uiState, vp);
      if (layout && uiState.focusedSystemId !== null) {
        const world = screenToWorld(uiState.systemCamera, layout.squareVp, p);
        const m = uiState.devSpawnMode;
        devSpawnEntity(state, {
          ownerEmpireId: m.ownerEmpireId,
          raceId: m.raceId,
          chassis: m.chassis,
          sx: world.x,
          sy: world.y,
          systemId: uiState.focusedSystemId,
        });
      }
      // Mode stays active — do NOT clear devSpawnMode here. Multi-click.
      return;
    }

    // M14 placement mode intercept — if a Build Space Structure button
    // started placement, the next left-click confirms the build at the
    // cursor's world coords. Fires before the normal selection logic
    // (ship/JP/planet picks) so we don't accidentally select while
    // placing.
    if (uiState.buildPlacement !== null && uiState.viewMode === 'system') {
      const layout = layoutSystemView(state, uiState, vp);
      if (layout && queueCommand) {
        const world = screenToWorld(uiState.systemCamera, layout.squareVp, p);
        const placement = uiState.buildPlacement;
        const constructor = state.ships.get(placement.constructorShipId);
        if (constructor && constructor.ownerEmpireId === state.playerEmpireId) {
          queueCommand({
            kind: 'buildSpaceStructure',
            empireId: state.playerEmpireId,
            constructorShipId: placement.constructorShipId,
            structureKind: placement.structureKind,
            sx: world.x,
            sy: world.y,
          });
        }
      }
      uiState.buildPlacement = null;
      return;
    }

    if (uiState.viewMode === 'galaxy') {
      const hit = hitTestSystems(
        uiState.galaxyCamera,
        vp,
        state.systems.values(),
        p,
        SYSTEM_HIT_RADIUS_PX,
      );
      // v3M18 — Connect Warp Gate target picker. While in connect-mode
      // and a same-empire operational unconnected gate sits in the hit
      // system, dispatch `connectWarpGate` and exit the mode. Clicking
      // the origin system itself, an empty space, or a system without
      // a valid target gate is a no-op (mode persists). Selection
      // change is suppressed during pick — the player's gate stays
      // selected in the detail panel.
      if (uiState.connectWarpGateMode !== null && queueCommand) {
        if (hit) {
          const fromGateId = uiState.connectWarpGateMode.fromGateId;
          const fromSystemId = uiState.connectWarpGateMode.fromSystemId;
          const fromGate = state.ships.get(fromGateId);
          if (fromGate && hit.id !== fromSystemId) {
            const ownerId = fromGate.ownerEmpireId;
            const linkedGateIds = new Set<number>();
            for (const link of state.warpGateLinks.values()) {
              linkedGateIds.add(link.gateAId);
              linkedGateIds.add(link.gateBId);
            }
            let targetGateId: ShipInstanceId | null = null;
            for (const s of state.ships.values()) {
              if (s.id === fromGateId) continue;
              if (s.ownerEmpireId !== ownerId) continue;
              if (s.chassis !== WARP_GATE_CHASSIS) continue;
              if (s.systemId !== hit.id) continue;
              if (linkedGateIds.has(s.id)) continue;
              targetGateId = s.id;
              break;
            }
            if (targetGateId !== null) {
              queueCommand({
                kind: 'connectWarpGate',
                empireId: ownerId,
                gateAId: fromGateId,
                gateBId: targetGateId,
              });
              uiState.connectWarpGateMode = null;
              return;
            }
          }
        }
        // Click on non-target — keep mode active. No selection change.
        return;
      }
      uiState.selection = hit ? { kind: 'system', id: hit.id } : { kind: 'none' };
      return;
    }

    const layout = layoutSystemView(state, uiState, vp);
    if (!layout) {
      if (!shift) {
        uiState.selection = { kind: 'none' };
        uiState.selectedShipIds.clear();
      }
      return;
    }

    // Priority: ship → linked JP (triggers jump) → planet → empty.
    const shipId = hitTestSystemViewShips(layout, p.x, p.y, SHIP_HIT_RADIUS_PX);
    if (shipId !== null) {
      const ship = state.ships.get(shipId);
      const isPlayer = !!ship && ship.ownerEmpireId === state.playerEmpireId;
      if (isPlayer) {
        if (shift) {
          if (uiState.selectedShipIds.has(shipId)) uiState.selectedShipIds.delete(shipId);
          else uiState.selectedShipIds.add(shipId);
        } else {
          uiState.selectedShipIds = new Set([shipId]);
        }
        reconcileSelectionWithShipSet();
      } else {
        // Clicking a non-player ship just focuses it in the detail panel.
        if (!shift) uiState.selectedShipIds.clear();
        uiState.selection = { kind: 'ship', id: shipId };
      }
      return;
    }

    // LMB on a jump point is a NO-OP by design (M8.2). Previously LMB-on-JP
    // v4M5.3b3c — JP-click handling retired with `state.jumpPoints`.
    // System-view background clicks now fall through directly to planet
    // / terrain / empty-click handlers.

    const planetId = hitTestSystemViewPlanets(layout, p.x, p.y);
    if (planetId !== null) {
      uiState.selection = { kind: 'planet', id: planetId };
      if (!shift) uiState.selectedShipIds.clear();
      return;
    }

    // v3M15.1 — terrain feature (asteroid field, gas giant, nebula, ion
    // cloud, radiation belt, black hole). Tested AFTER ships and planets
    // so they win on overlap (a ship sitting in an asteroid field still
    // selects as a ship; a planet partially over an environment footprint
    // still selects as a planet).
    const envId = hitTestSystemViewEnvironment(layout, p.x, p.y);
    if (envId !== null) {
      uiState.selection = { kind: 'environment', id: envId };
      if (!shift) uiState.selectedShipIds.clear();
      return;
    }

    // Empty click — clear both selection tracks (unless shift is held, in
    // which case we leave the accumulated ship-set alone).
    if (!shift) {
      uiState.selection = { kind: 'none' };
      uiState.selectedShipIds.clear();
    }
  }

  function handleRightClick(p: { x: number; y: number }, shift: boolean): void {
    // v3M18 — Connect Warp Gate target-picker cancel. RMB exits the
    // mode (matches M14 placement-mode behaviour). Fires before every
    // other RMB intent.
    if (uiState.connectWarpGateMode !== null) {
      uiState.connectWarpGateMode = null;
      return;
    }
    // M14 placement-mode cancel — RMB aborts a pending build without
    // needing the keyboard. Fires before any other RMB intent.
    if (uiState.buildPlacement !== null) {
      uiState.buildPlacement = null;
      return;
    }
    // v3M22.4 — Guard target-picker cancel. RMB exits the mode without
    // committing a guard target.
    if (uiState.guardTargetMode !== null) {
      uiState.guardTargetMode = null;
      return;
    }
    // v4M5.3b3d/e — galaxy-view RMB on a destination system with
    // player ships selected dispatches `moveAndJumpShips`. The cursor's
    // offset from the destination star centre drives the arrivalAngle
    // — clicking on the star centre falls back to "from source" (null),
    // clicking off-centre picks the angle on the destination's r=0.48
    // ring that points at the click position. Lets the player aim the
    // arrival without a separate drag gesture.
    if (uiState.viewMode === 'galaxy') {
      if (!queueCommand) return;
      if (uiState.selectedShipIds.size === 0) return;
      const state = getGameState();
      const vp = getViewport();
      const hit = hitTestSystems(
        uiState.galaxyCamera,
        vp,
        state.systems.values(),
        { x: p.x, y: p.y },
        SYSTEM_HIT_RADIUS_PX,
      );
      if (!hit) return;
      // Resolve source system from the first selected player ship.
      let sourceSystemId: number | null = null;
      const shipIds: ShipInstanceId[] = [];
      for (const id of uiState.selectedShipIds) {
        const ship = state.ships.get(id);
        if (!ship) continue;
        if (ship.ownerEmpireId !== state.playerEmpireId) continue;
        if (ship.systemId === null) continue;
        if (sourceSystemId === null) sourceSystemId = ship.systemId;
        if (ship.systemId !== sourceSystemId) continue;
        shipIds.push(id);
      }
      if (sourceSystemId === null) return;
      if (shipIds.length === 0) return;
      if (hit.id === sourceSystemId) return; // can't jump to your own system
      // Compute the arrival angle from the cursor's offset to the
      // destination star (in screen space — equivalent up to the camera
      // transform). A near-centre click yields a near-zero offset; we
      // fall back to "from source" (null) when the click is closer
      // than ~25 px to the star centre so the picker isn't twitchy on
      // a centred click.
      const hitScreen = (() => {
        const c = uiState.galaxyCamera;
        const cx = vp.padLeft + vp.viewW / 2 + (hit.x - c.centerX) * c.zoom * vp.viewW;
        const cy = vp.padTop + vp.viewH / 2 + (hit.y - c.centerY) * c.zoom * vp.viewH;
        return { x: cx, y: cy };
      })();
      const odx = p.x - hitScreen.x;
      const ody = p.y - hitScreen.y;
      const offset = Math.hypot(odx, ody);
      const arrivalAngle = offset > 25 ? Math.atan2(ody, odx) : null;
      queueCommand({
        kind: 'moveAndJumpShips',
        shipIds,
        targetSystemId: hit.id,
        arrivalAngle,
      });
      return;
    }
    // Right-click in tactical view with a non-empty player-ship selection
    // issues `moveShips` — plain sublight order to the cursor position.
    // (System-view RMB-on-JP gesture retired at v4M5.3b3c.)
    if (uiState.viewMode !== 'system') return;
    if (!queueCommand) return;
    if (uiState.selectedShipIds.size === 0) return;
    const focusedSystemId = uiState.focusedSystemId;
    if (focusedSystemId === null) return;

    const state = getGameState();
    const vp = getViewport();
    const layout = layoutSystemView(state, uiState, vp);
    if (!layout) return;

    const shipIds: ShipInstanceId[] = [];
    for (const id of uiState.selectedShipIds) {
      const ship = state.ships.get(id);
      if (!ship) continue;
      if (ship.ownerEmpireId !== state.playerEmpireId) continue;
      if (ship.systemId !== focusedSystemId) continue;
      shipIds.push(id);
    }
    if (shipIds.length === 0) return;

    // v3M18 — RMB on a player-owned Warp Gate that is part of a live
    // warp link dispatches `jumpShipsViaWarpGate`. Checked before JP
    // and planet hits so a gate sitting close to either still wins.
    // Hit-test reuses the standard ship picker; we then validate
    // chassis / ownership / link state.
    const gateHitId = hitTestSystemViewShips(layout, p.x, p.y, SHIP_HIT_RADIUS_PX);
    if (gateHitId !== null) {
      const gate = state.ships.get(gateHitId);
      if (
        gate &&
        gate.chassis === WARP_GATE_CHASSIS &&
        gate.ownerEmpireId === state.playerEmpireId
      ) {
        let liveLinkId: number | null = null;
        for (const link of state.warpGateLinks.values()) {
          if (link.status !== 'live') continue;
          if (link.gateAId !== gate.id && link.gateBId !== gate.id) continue;
          liveLinkId = link.id;
          break;
        }
        if (liveLinkId !== null) {
          queueCommand({ kind: 'jumpShipsViaWarpGate', shipIds, linkId: liveLinkId });
          return;
        }
      }
    }

    // v4M5.3b3c — JP click handling retired alongside `state.jumpPoints`.
    // System-view RMB no longer initiates inter-system jumps; the
    // v4M5.3b3d picker UI will add RMB-on-destination-system in galaxy
    // view as the replacement gesture.

    // RMB on a planet:
    //   - Uninhabited (Pop=0, owner=null) AND a single Coloniser-class ship
    //     selected → moveAndColonise (MFix3.4). The ship sails to the planet
    //     and auto-colonises on arrival.
    //   - Enemy or neutral (other-owner) planet → bombardPlanet (v2M5,
    //     PLANETS §2.7).
    //   - Own planet → fall through to moveShips so a player can still
    //     order their ships near their own world without bombarding it.
    const planetId = hitTestSystemViewPlanets(layout, p.x, p.y);
    if (planetId !== null) {
      const planet = state.planets.get(planetId);
      if (planet) {
        const isUninhabited =
          planet.ownerEmpireId === null && planet.currentPopulation === 0;
        // Colonise gesture: exactly one ship selected, that ship is a
        // Coloniser-class (template specialAbility), planet uninhabited.
        if (isUninhabited && shipIds.length === 1) {
          const shipId = shipIds[0]!;
          const ship = state.ships.get(shipId);
          if (ship) {
            const empire = state.empires.get(ship.ownerEmpireId);
            const tmpl = empire
              ? state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`)
              : undefined;
            if (tmpl && tmpl.specialAbilities.includes('Colonise planet')) {
              queueCommand({
                kind: 'moveAndColonise',
                empireId: state.playerEmpireId,
                shipId,
                targetPlanetId: planetId,
              });
              return;
            }
          }
        }
        // Otherwise: bombard if planet is owned by someone else.
        if (planet.ownerEmpireId !== state.playerEmpireId) {
          queueCommand({
            kind: 'bombardPlanet',
            empireId: state.playerEmpireId,
            shipIds,
            targetPlanetId: planetId,
          });
          return;
        }
        // Own planet — fall through to moveShips below.
      }
    }

    // Convert click → system-space using the same square viewport the
    // renderer used, so what-you-click is where ships go.
    const sp = screenToWorld(uiState.systemCamera, layout.squareVp, p);

    // v3M22.4 — Shift+RMB on empty space appends a waypoint to every
    // selected ship's queue. Skips the standard moveShips dispatch
    // entirely. The ship's `commandKind` stays unchanged — the player
    // composes a chain via Shift+RMB then clicks Patrol when ready.
    if (shift) {
      for (const shipId of shipIds) {
        queueCommand({
          kind: 'appendShipWaypoint',
          empireId: state.playerEmpireId,
          shipId,
          targetX: sp.x,
          targetY: sp.y,
        });
      }
      return;
    }

    // v3M22.4 — Plain RMB cancels any active v3M22 high-level command
    // ("go here NOW and forget what you were doing") then issues the
    // standard single-shot move. clearShipCommand resets commandKind +
    // waypoints + guard target; moveShips writes the moveOrder.
    for (const shipId of shipIds) {
      queueCommand({
        kind: 'clearShipCommand',
        empireId: state.playerEmpireId,
        shipId,
      });
    }
    queueCommand({ kind: 'moveShips', shipIds, targetX: sp.x, targetY: sp.y });
  }

  function onDoubleClick(e: MouseEvent): void {
    // Double-click in galaxy view drills into the system under the cursor.
    if (uiState.viewMode !== 'galaxy') return;
    const p = localMousePos(e);
    const state = getGameState();
    const vp = getViewport();
    const hit = hitTestSystems(
      uiState.galaxyCamera,
      vp,
      state.systems.values(),
      p,
      SYSTEM_HIT_RADIUS_PX,
    );
    if (!hit) return;
    // v4M10.3 — double-click drill-in is unconditional per `FOW.md §3.4`.
    // The v4M6.5 hard gate on unscouted systems is retired: entry is
    // allowed and the renderer's Unexplored branch handles the visual.
    uiState.focusedSystemId = hit.id;
    uiState.selection = { kind: 'system', id: hit.id };
    // Reset the in-system camera so the tactical map opens framed.
    uiState.systemCamera = defaultSystemCamera();
    startViewTransition(uiState, 'system', now());
  }

  function onKeyDown(e: KeyboardEvent): void {
    // MFix42.7 — binding dispatch helper. Fetched once per keydown so a
    // long sequence of branches reads cleanly. Falls back to
    // DEFAULT_KEYBINDINGS via `getKeyForAction` when the settings layer
    // is missing entries (per-action sparse override).
    const bindings = getControlsSettings?.().keyBindings;
    const matches = (action: ActionId): boolean =>
      bindingMatchesEvent(getKeyForAction(bindings, action), e);

    // v2M3 game-speed hotkeys per UI_UX §5. Wired before Escape so the
    // ordering reflects "transient gameplay control" → "menu". Speed
    // hotkeys silently no-op when no controller is wired (tests).
    if (matches('togglePause')) {
      // Single-player only per UI_UX §5; MP build will gate on game mode.
      speedController?.toggleUserPause();
      e.preventDefault();
      return;
    }
    if (matches('speedUp')) {
      speedController?.cycleSpeedUp();
      return;
    }
    if (matches('speedDown')) {
      speedController?.cycleSpeedDown();
      return;
    }
    if (e.key === 'Escape') {
      // Priority order (UI_UX §9.3):
      //   1. Cancel the Dev Toolbox spawn-entity mode (MFix11.3).
      //   2. Cancel an in-flight Connect Warp Gate target picker.
      //   3. Cancel an in-flight guard-target picker.
      //   4. Cancel an in-flight build placement.
      //   5. Clear any active selection / marquee.
      //   6. With nothing to dismiss, open the in-game menu.
      if (uiState.devSpawnMode !== null) {
        exitDevSpawnMode(uiState);
        return;
      }
      if (uiState.connectWarpGateMode !== null) {
        uiState.connectWarpGateMode = null;
        return;
      }
      if (uiState.guardTargetMode !== null) {
        uiState.guardTargetMode = null;
        return;
      }
      if (uiState.buildPlacement !== null) {
        uiState.buildPlacement = null;
        return;
      }
      const hadSelection =
        uiState.selection.kind !== 'none' ||
        uiState.selectedShipIds.size > 0 ||
        uiState.marquee !== null;
      if (hadSelection) {
        uiState.selection = { kind: 'none' };
        uiState.selectedShipIds.clear();
        uiState.marquee = null;
        return;
      }
      onOpenMenu?.();
      return;
    }
    if (matches('toggleGalaxy')) {
      // v4M1.5 — auto-repeat keydown events (held key) fire on most
      // browsers. Ignore those for the hold-to-preview machinery; only
      // act on the FIRST keydown.
      if (e.repeat) return;
      if (uiState.viewMode !== 'galaxy') {
        // Reset galaxy zoom so we don't immediately trip the threshold again.
        uiState.galaxyCamera = {
          ...uiState.galaxyCamera,
          zoom: clampZoom(Math.min(uiState.galaxyCamera.zoom, 2)),
        };
        // v4M1.5 — hold-to-preview opt-in. Remember the focused system
        // so keyup can restore. Toggle mode (default) leaves the
        // player in galaxy view until they zoom or click in.
        if (getControlsSettings?.().holdGForGalaxyPreview === true) {
          holdGPreview.previousSystemId = uiState.focusedSystemId;
          holdGPreview.active = true;
        }
        startViewTransition(uiState, 'galaxy', now());
      }
      return;
    }
    if (matches('toggleCloak')) {
      // Toggle cloak on the current ship selection. The applier filters out
      // non-eligible ships, so passing the raw selection is safe — no need
      // to pre-filter here. Skip the emit entirely when nothing is selected
      // or no command sink is wired (e.g. tests that mount input without a
      // sim loop).
      if (!queueCommand) return;
      if (uiState.selectedShipIds.size === 0) return;
      const state = getGameState();
      queueCommand({
        kind: 'toggleCloak',
        shipIds: [...uiState.selectedShipIds],
        empireId: state.playerEmpireId,
      });
      return;
    }
    // v4M6.2 — strategic-overlay hotkeys per `docs/MAP.md §3.4`. P / T
    // / E / D map to the four overlays; re-pressing the same letter
    // toggles back to `'none'`. Plain digits aren't used because they
    // collide with the v3M16 fleet-recall pattern (0..9). Skipped when
    // a text input is focused so the new-game-setup / save-picker
    // dialogs don't hijack the keys.
    // MFix42.7 — dispatch via binding map.
    {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
        const overlayKey: 'production' | 'threat' | 'exploration' | 'diplomatic' | null =
          matches('overlayProduction')
            ? 'production'
            : matches('overlayThreat')
              ? 'threat'
              : matches('overlayExploration')
                ? 'exploration'
                : matches('overlayDiplomatic')
                  ? 'diplomatic'
                  : null;
        if (overlayKey !== null) {
          e.preventDefault();
          const next = uiState.galaxyOverlay === overlayKey ? 'none' : overlayKey;
          uiState.galaxyOverlay = next;
          saveGalaxyOverlay(next);
          return;
        }
      }
    }
    // v3M16 — Fleet (control-group) bindings. Ctrl+0..9 captures the
    // current ship selection into the matching fleet slot. 0..9 alone
    // recalls the slot's contents into the active selection; a second
    // press of the SAME digit within FLEET_DOUBLE_TAP_MS jumps the
    // camera to the fleet (classic RTS double-tap-N gesture). Skipped
    // when a text input is focused so settings / save-slot dialogs
    // don't hijack number keys.
    if (e.key.length === 1 && e.key >= '0' && e.key <= '9') {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        // Defer to the browser's text-entry behaviour.
        return;
      }
      const fleetId = Number(e.key);
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        assignFleet(uiState, fleetId);
        // Assign breaks any in-flight double-tap chain — the player's
        // intent shifted from view-recall to slot-write.
        lastFleetTapId = null;
      } else if (!e.altKey && !e.shiftKey) {
        e.preventDefault();
        const state = getGameState();
        const t = now();
        if (
          lastFleetTapId === fleetId &&
          t - lastFleetTapTime <= FLEET_DOUBLE_TAP_MS
        ) {
          // Second tap within the window — jump the camera. Selection
          // is unaffected (the first tap already recalled).
          centerCameraOnFleet(uiState, state, fleetId, t);
          lastFleetTapId = null;
        } else {
          // First tap (or outside the window, or different fleet) —
          // recall and arm the double-tap detector for next press.
          recallFleet(uiState, state, fleetId);
          lastFleetTapId = fleetId;
          lastFleetTapTime = t;
        }
      }
      return;
    }
    if (matches('devDetectionOverlay')) {
      // Dev-only detection overlay toggle (M8-F). Strictly local UI state,
      // never touches the command stream — so it's safe from MULTIPLAYER §2
      // determinism concerns and won't desync peers.
      e.preventDefault();
      uiState.devDetectionOverlay = !uiState.devDetectionOverlay;
      return;
    }
    if (matches('devEnemyPov')) {
      // Dev-only enemy-POV overlay toggle (M8.1-F). Swaps the renderer's
      // viewer empire to the first non-player empire so fog of war reflects
      // what the enemy sees. Same MULTIPLAYER §2 reasoning as F9 — purely
      // local UI state, zero sim side effects.
      e.preventDefault();
      uiState.enemyPovOverlay = !uiState.enemyPovOverlay;
      return;
    }
    if (matches('devToolbox')) {
      // v3M13 — backtick toggles the Dev Toolbox panel. Bundles F9 / F10 /
      // reveal-all checkboxes plus per-resource +10k buttons. Strictly local
      // UI state. The panel mounts/unmounts based on uiState.devToolboxOpen
      // via a per-frame DOM sync in canvas.ts (mirrors the research-panel
      // mount pattern).
      e.preventDefault();
      uiState.devToolboxOpen = !uiState.devToolboxOpen;
      return;
    }
  }

  function onContextMenu(e: MouseEvent): void {
    // Suppress the browser's right-click menu — the right mouse button is
    // reserved for issuing move orders in tactical view (onMouseUp).
    e.preventDefault();
  }

  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('dblclick', onDoubleClick);
  canvas.addEventListener('mouseleave', onMouseLeave);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('keydown', onKeyDown);
  // v4M1.5 — keyup listener for hold-G-to-preview-galaxy. Inert when
  // the toggle is off; the keydown handler arms the state machine.
  window.addEventListener('keyup', onKeyUp);
  canvas.addEventListener('contextmenu', onContextMenu);

  return {
    destroy(): void {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('dblclick', onDoubleClick);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      canvas.removeEventListener('contextmenu', onContextMenu);
    },
  };
}
